import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import axios from "axios";
import type { ApiResponse } from "../types";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
    ? connection_development
    : process.env.REACT_APP_NODE_ENV === "production"
    ? connection_production
    : connection_local;

/**
 * User
 */
export const retrieveTokenInformation = async (params): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.authenticate.context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveTokenInformationWithGoogle = async (params): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.authenticate["with-google"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveTokenInformationWithProfileByExternalResource = async (
  params
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.authenticate["with-profile"]["by-external-resource"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveUserInformation = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["user-from-token"].context}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * These two reads run on a bare axios instance (no interceptor) at boot and at every SSO hand-off, where a
 * hung backend would otherwise hold the spinner until the browser gives up — minutes, with no message. Past
 * this the request rejects with no `response`, which both callers already treat as "server unreachable".
 */
const TOKEN_READ_TIMEOUT_MS = 30000;

export const retrieveUserInformationFromToken = async (accessToken): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.get(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["user-from-token"].context}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        timeout: TOKEN_READ_TIMEOUT_MS,
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveAccessTokenFromRefreshToken = async (refreshToken): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["refresh-token"].context}`,
      {
        refreshToken,
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveAccessTokenFromRefreshTokenWithExpiration = async (
  refreshToken,
  userProfileId
): Promise<ApiResponse> => {
  try {
    const response = await axios
      .create()
      .post(
        `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["refresh-token-from-expiration"].context}`,
        {
          refreshToken,
          userProfileId,
        }
      );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveUserProfileTokenInformation = async (
  profileId,
  refreshToken
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.authenticate["with-profile"].context}/${profileId}`,
      {
        refreshToken,
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const createBasicUser = async (user): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.basic.context}`,
      user
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const patchUser = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.management.url}${endpoints.management.users.context}/`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const patchUserOnlyRequiredAction = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.patch(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["only-requiredAction"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const sendForgotPasswordEmail = async (
  userDTO,
  verifyFirst = false
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    // verifyFirst=true opts into the verify-email-first backend behavior (unverified accounts get an
    // email-verification code instead of the reset code); the response { emailVerified } tells the caller
    // which was sent. Older backends ignore the param and return an empty body (emailVerified undefined).
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.send.email["forgot-password"].context}?verifyFirst=${verifyFirst}`,
      userDTO
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

// Ported from catchup-admin-web — asks the backend to email a fresh
// EMAIL_VERIFICATION code to the signed-in user. Pairs with patchAndVerifyUserEmail
// (which submits the entered code). The learner Support screen's verify-email gate
// uses these two together.
export const sendEmailVerificationEmail = async (
  userDTO,
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.send.email["email-verification"].context}`,
      userDTO,
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const patchAndVerifyUserEmail = async (params): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.patch(
      `${connection.base.management.url}${endpoints.management.generated.code.context}${endpoints.management.generated.code["email-verification"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * User Profile
 */
export const retrieveUserProfileList = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.management.url}${endpoints.management.users.profiles.context}${endpoints.management.users.profiles.dto.context}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveUserProfileListFromToken = async (accessToken): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.get(
      `${connection.base.management.url}${endpoints.management.users.profiles.context}${endpoints.management.users.profiles.dto.context}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        timeout: TOKEN_READ_TIMEOUT_MS,
      }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrieveUserProfileDTOById = async (userProfileId): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.management.url}${endpoints.management.users.profiles.context}${endpoints.management.users.profiles.dto.id.context}`.replace(
        "{id}",
        userProfileId
      )
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const queryUserProfileDTOListBySeasonIdAndSearchName = async (
  queryParams
): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.profiles.context}${endpoints.management.users.profiles.query.dto["by-seasonId-searchName"].context}`,
      queryParams
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Price Group
 */

/**
 * User Email
 */
export const patchAndForgotUserPassword = async (params): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.patch(
      `${connection.base.management.url}${endpoints.management.generated.code.context}${endpoints.management.generated.code["forgot-password"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Integrations
 */

/**
 * @deprecated Superseded by `loginWithK12AuthorizationCode` below, which exchanges the code and mints the
 * session in ONE server-side call. This one returns the K12 user id to the browser, which then has to post
 * it to `retrieveTokenInformationWithProfileByExternalResource` — an endpoint that accepts an identity
 * claim from any caller. No screen calls either of them any more.
 *
 * Kept, rather than deleted, only because the BACKEND endpoints behind them are still live for the mobile
 * app, which has not been migrated. Remove both once mobile is on `/k12/login`.
 */
export const retrieveK12UserByAuthorizationCode = async (params): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.integrations.context}${endpoints.management.integrations.k12.users["by-authorization-code"].context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Exchanges SEBIT VCloud's hand-off token for the URL that lands the user signed in.
 *
 * A bare `axios.create()` with no interceptors, like the K12 call above: this runs before any Catchup token
 * exists, so the global request interceptor would have nothing to attach and the 401 retry path has nothing
 * to refresh.
 *
 * The token goes in the BODY rather than the query string. It is a bearer credential for its whole ~5-minute
 * life, and a query string is written to access logs, proxy logs and `Referer` headers on the way through.
 */
/**
 * The K12Net SSO login leg — one call that exchanges the authorization code and hands back where to send
 * the now-signed-in user.
 *
 * **Replaces the two-hop flow** (`retrieveK12UserByAuthorizationCode` then
 * `retrieveTokenInformationWithProfileByExternalResource`). That pair handed the K12 user id to this
 * browser and then posted it back to an endpoint that accepts an identity claim from anyone, so knowing a
 * pupil's K12 GUID was enough to obtain their session. Here the identity never leaves the server.
 *
 * A BARE axios instance, like `loginWithSebitToken` below and for the same reason: this runs before any
 * Catchup session exists.
 */
export const loginWithK12AuthorizationCode = async (
  params
): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.integrations.context}${endpoints.management.integrations.k12.login.context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const loginWithSebitToken = async (params): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.integrations.context}${endpoints.management.integrations.sebit.login.context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * User Profile Friendship
 *
 * Learner-to-learner friendships behind the "Çevrimiçi arkadaşlar" panel. The acting learner and their
 * season are taken from the token server-side, so none of these bodies carry an identity.
 */
export const sendFriendRequest = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.request.context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const acceptFriendRequest = async (friendshipId): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.id.accept.context.replace(
        "{id}",
        friendshipId
      )}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const declineFriendRequest = async (friendshipId): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.id.decline.context.replace(
        "{id}",
        friendshipId
      )}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/** Cancels an outgoing request, or unfriends an accepted friendship. */
export const deleteFriendship = async (friendshipId): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.id.context.replace(
        "{id}",
        friendshipId
      )}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * The caller's accepted friends, from the friendship system of record. Carries the friendshipId (needed to
 * unfriend) and the grade/branch line; presence and mascots come from catchtivity's online-peers instead,
 * which is why the panel merges the two by userProfileId.
 */
export const retrieveFriendList = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.query.friends.dto.context}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const retrievePendingFriendRequests = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.query.pending.dto.context}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const searchFriendCandidates = async (params): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.query.candidates.dto.context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * P7 — "Show my status to friends". The learner is taken from the token, so neither call carries an
 * identity. When off, friends see the caller as offline and a refused invite says only "not available".
 */
export const retrieveFriendStatusVisibility = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.get(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.me["status-visibility"].context}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

export const updateFriendStatusVisibility = async (showStatusToFriends: boolean): Promise<ApiResponse> => {
  try {
    const response = await axios.put(
      `${connection.base.management.url}${endpoints.management.users.friendships.context}${endpoints.management.users.friendships.me["status-visibility"].context}`,
      { showStatusToFriends }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Impersonation ("Kullanıcı olarak görüntüle") — receive side.
 *
 * app-web never STARTS an impersonation; only admin-web can, and only an ADMIN there. These two are the
 * redeem and the audit-close.
 */
export const redeemImpersonationHandoffCode = async (
  params
): Promise<ApiResponse> => {
  try {
    // A LOCAL axios instance: the redeeming tab has no session yet, and the global interceptor would stamp
    // whatever token happens to be in localStorage. Unauthenticated is the honest shape — the single-use
    // code IS the credential.
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.impersonation.redeem.context}`,
      params
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/** Ends the caller's OWN impersonation. The session id comes from the token's `act` claim, not the body. */
export const endOwnImpersonation = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users.impersonation.end.context}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * Mobile QR sign-in — mint side. Returns `{ qrLoginCode, expireAt }` for the CALLER'S OWN profile; the
 * mobile app scans `catchupper://qr-login/<qrLoginCode>` and redeems it for its own session.
 *
 * Authenticated through the global interceptor. The backend derives the subject from the access token, so a
 * caller can only ever hand over the session it already has; the body carries the caller's OWN refresh token
 * only as proof that session is still live (an access token alone outlives a password change / forced
 * logout by hours). The code is single-use and lives 60 seconds — never put a token of any kind in a QR.
 * Also minted by TakePhotoGetAnswerView (the photo-solve QR, which used to embed the raw access token);
 * that QR opens a WEB page, which redeems through redeemQrLoginCode below.
 */
export const createQrLoginCode = async (): Promise<ApiResponse> => {
  try {
    const response = await axios.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["qr-login-code"].context}`,
      { refreshToken: localStorage.getItem("refreshToken") }
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};

/**
 * QR sign-in — redeem side, for the web page a QR opens on the phone (Fotoğrafı çek çözümü al →
 * /solve/activity/:language/:qrLoginCode). Spends the single-use, 60-second code for a session of the
 * profile that minted it. A LOCAL axios instance, like redeemImpersonationCode: the phone tab has no
 * session, and the global interceptor would stamp (or try to refresh) whatever sits in this browser's
 * localStorage — which may be somebody else's.
 */
export const redeemQrLoginCode = async (code: string): Promise<ApiResponse> => {
  try {
    const axiosInstance = axios.create();
    const response = await axiosInstance.post(
      `${connection.base.management.url}${endpoints.management.users.context}${endpoints.management.users["qr-login"].context.replace(
        "{code}",
        encodeURIComponent(code)
      )}`
    );
    return { data: response.data, status: response.status };
  } catch (err) {
    return { err };
  }
};
